import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import type {ColumnDef} from "@tanstack/react-table";
import {DataFilterOperator} from "crqlar-react-common/dist/models/audience/filter.model";
import type {LifecycleMail} from "crqlar-react-common/dist/models/newsletter/newsletterMail";
import {NewsletterMailStatus} from "crqlar-react-common/dist/models/newsletter/newsletterMail";
import {
    DaysBeforeSend,
    LifeCycleMailType,
    NewsletterMailType,
} from "crqlar-react-common/dist/models/newsletter/newsletterMailType";
import {NewsletterLifecycleMailService} from "crqlar-react-common/dist/services/newsletter/newsletterLifecycleMail.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useNewsletterAccountQuery} from "crqlar-react-common/dist/utils/hooks/newsletter/useNewsletterAccount";
import {useLifecycleMailFilterOptions} from "crqlar-react-common/dist/utils/hooks/newsletter/useNewsletterMails";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQueryClient} from "react-query";
import {
    RightSidebarCreateButton,
    RightSidebarDivider,
    RightSidebarRail,
    RightSidebarRailLayout,
} from "../../../layout/rightSidebar/RightSidebarRail";
import {
    ServerTable,
    ServerTableFrame,
    ServerTableProvider,
    ServerTableRailColumns,
    ServerTableRailFilter,
    ServerTableRailFullscreen,
    ServerTableRailSearch,
    ServerTableRailSort,
    useServerTable,
} from "../../../common/serverTable";
import {DateElement} from "../../../common/uiElements/DateElement";
import {MaileonSnackbar} from "../../../../utils/alert/MaileonSnackbar";
import CreateLifecycleMailDialog from "./CreateLifecycleMailDialog";

function LifecycleMailTimingCell({mail}: {mail: LifecycleMail}) {
    const time = mail.dailyExecutionTime?.substring(0, 5);

    if (mail.daysBeforeSend === undefined || !time) return null;

    return <Box>{`${mail.daysBeforeSend} days, at ${time}`}</Box>;
}

const LifecycleMailsList = () => {
    useTrackScreenVisit("lifecycle mails");

    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const readOnlyUser = useReadOnly();
    const newsletterAccountQuery = useNewsletterAccountQuery();
    const filterOptionsQuery = useLifecycleMailFilterOptions();
    const [currentMailing, setCurrentMailing] = useState<LifecycleMail>();
    const [snackbar, setSnackbar] = useState<{
        open: boolean
        message: string
        severity: "success" | "error" | "info" | "warning"
    }>({
        open: false,
        message: "",
        severity: "info",
    });

    const refreshTable = useCallback(
        () => queryClient.invalidateQueries(["server-table", "marketing-lifecycle-mails"]),
        [queryClient],
    );
    const handleCreateMailing = useCallback(() => {
        setCurrentMailing({
            status: NewsletterMailStatus.DRAFT,
            type: NewsletterMailType.LIFECYCLE,
            subType: Object.values(LifeCycleMailType)[0],
            daysBeforeSend: Object.values(DaysBeforeSend)[0],
            dailyExecutionTime: "12:00",
        });
    }, []);
    const handleNavigateToMaileon = useCallback((maileonMailingId?: string) => {
        const loginUrl = newsletterAccountQuery.data?.newsletterAccountLoginUrl;
        if (!loginUrl || !maileonMailingId) return;

        window.open(
            `${loginUrl}/eagle_kp_webapp/mln/campaigns/triggers?eId=${maileonMailingId}`,
            "_blank",
            "noopener,noreferrer",
        );
    }, [newsletterAccountQuery.data?.newsletterAccountLoginUrl]);

    const columns = useMemo<ColumnDef<LifecycleMail>[]>(() => [
        {
            id: "status",
            header: t("Status"),
            accessorFn: mail => mail.status,
            cell: ({row}) => (
                <Box>
                    {row.original.status
                        ? t(`Lifecycle mail status ${row.original.status}`)
                        : null}
                </Box>
            ),
            size: 180,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "name",
            header: t("Name"),
            accessorFn: mail => mail.name,
            cell: ({row}) => <Box>{row.original.name ?? null}</Box>,
            size: 280,
            meta: {defaultActive: true, isServerSortable: true, grow: true},
        },
        {
            id: "subType",
            header: t("Subtype"),
            accessorFn: mail => mail.subType,
            cell: ({row}) => <Box>{row.original.subType ?? null}</Box>,
            size: 220,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: "type",
            },
        },
        {
            id: "timing",
            header: t("Timing"),
            accessorFn: mail => `${mail.daysBeforeSend ?? ""}-${mail.dailyExecutionTime ?? ""}`,
            cell: ({row}) => <LifecycleMailTimingCell mail={row.original}/>,
            size: 200,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                serverSortField: "type",
            },
        },
        {
            id: "mailsSent",
            header: t("Mails sent"),
            accessorFn: mail => mail.mailsSent,
            cell: ({row}) => <Box>{row.original.mailsSent ?? null}</Box>,
            size: 140,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "openRate",
            header: t("Open rate"),
            accessorFn: mail => mail.openRate,
            cell: ({row}) => <Box>{row.original.openRate ?? null}</Box>,
            size: 140,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "clickRate",
            header: t("Click rate"),
            accessorFn: mail => mail.clickRate,
            cell: ({row}) => <Box>{row.original.clickRate ?? null}</Box>,
            size: 140,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "filteredContacts",
            header: t("Filtered contacts"),
            accessorFn: mail => mail.filteredContacts,
            cell: ({row}) => <Box>{row.original.filteredContacts ?? null}</Box>,
            size: 180,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "bounces",
            header: t("Bounces"),
            accessorFn: mail => mail.bounces,
            cell: ({row}) => <Box>{row.original.bounces ?? null}</Box>,
            size: 140,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "unsubscriptions",
            header: t("Unsubscriptions"),
            accessorFn: mail => mail.unsubscriptions,
            cell: ({row}) => <Box>{row.original.unsubscriptions ?? null}</Box>,
            size: 180,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "actions",
            header: t("Actions"),
            cell: ({row}) => {
                const canOpenMaileon = Boolean(
                    newsletterAccountQuery.data?.newsletterAccountLoginUrl
                    && row.original.maileonMailingId,
                );

                return (
                    <Box data-no-row-click>
                        <Button
                            variant="contained"
                            disabled={!canOpenMaileon}
                            onClick={() => handleNavigateToMaileon(row.original.maileonMailingId)}
                        >
                            {t("Edit")}
                        </Button>
                    </Box>
                );
            },
            size: 120,
            enableResizing: false,
            meta: {defaultActive: true},
        },
        {
            id: "id",
            header: t("ID"),
            accessorFn: mail => mail.id,
            cell: ({row}) => <Box>{row.original.id ?? null}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true},
        },
        {
            id: "updateDate",
            header: t("Last updated"),
            accessorFn: mail => mail.updateDate,
            cell: ({row}) => <DateElement date={row.original.updateDate}/>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true},
        },
    ], [handleNavigateToMaileon, newsletterAccountQuery.data?.newsletterAccountLoginUrl, t]);

    const controller = useServerTable<LifecycleMail>({
        tableId: "marketing-lifecycle-mails",
        columns,
        getRowId: mail => mail.id!,
        onRowClick: mail => setCurrentMailing(mail),
        initialSorting: [{id: "id", desc: true}],
        queryFn: async ({pagination, searchQuery, filterRules, sorting}) => {
            const page = await NewsletterLifecycleMailService.listLifecycleEmails({
                query: searchQuery || undefined,
                advancedFilter: filterRules.length
                    ? {
                        filterGroups: [{
                            filterRules,
                            operator: DataFilterOperator.AND,
                        }],
                    }
                    : undefined,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined,
                },
            });

            return {
                content: page.content ?? [],
                totalElements: page.totalElements ?? 0,
            };
        },
    });

    return (
        <>
            <MaileonSnackbar
                snackbar={snackbar}
                setSnackbar={setSnackbar}
                t={t}
            />

            <Box sx={{display: "flex", flex: 1, minWidth: 0, minHeight: 0}}>
                <ServerTableProvider controller={controller}>
                    <ServerTableFrame>
                        <RightSidebarRailLayout>
                            <ServerTable/>

                            <RightSidebarRail>
                                <ServerTableRailSearch/>
                                <ServerTableRailSort/>
                                <ServerTableRailFilter
                                    options={filterOptionsQuery.data ?? []}
                                    disabled={filterOptionsQuery.isLoading || filterOptionsQuery.isError}
                                />
                                <ServerTableRailFullscreen/>
                                <ServerTableRailColumns/>
                                <RightSidebarDivider/>
                                <RightSidebarCreateButton
                                    label={t("Create Lifecycle Mail")}
                                    disabled={readOnlyUser}
                                    onClick={handleCreateMailing}
                                />
                            </RightSidebarRail>
                        </RightSidebarRailLayout>
                    </ServerTableFrame>
                </ServerTableProvider>
            </Box>

            <CreateLifecycleMailDialog
                key={currentMailing ? currentMailing.id ?? "new" : "closed"}
                open={!!currentMailing}
                onClose={() => setCurrentMailing(undefined)}
                refetchLifecycleMails={refreshTable}
                mailToUpdate={currentMailing}
                setMailToUpdate={setCurrentMailing}
                showSnackbar={(message, severity) => setSnackbar({
                    open: true,
                    message,
                    severity,
                })}
            />
        </>
    );
};

export default LifecycleMailsList;
